/**
 * ProfileDraftSheet — bottom-anchored editor for the live build_profile card
 * (revamp T24), the ThesisDraftSheet's simplified twin: four chip rows (style,
 * risk, turnover, knowledge), no free-typed fields. Every tap calls onChange
 * with a single-field patch; the parent applies it optimistically — the agent
 * sees the merged card on the next turn via the transcript. Save is enabled
 * iff the three required dimensions are set (profileSaveEnabled).
 */
import React, { useMemo } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ARCHETYPES, TURNOVER_TOLERANCES } from '../../config/souls';
import { KNOWLEDGE_LEVELS, RISK_LEVELS, profileSaveEnabled } from '../../services/profileCard';
import type { ProfileCard } from '../../services/chatStream';

const KNOWLEDGE_LABEL_KEYS: Record<(typeof KNOWLEDGE_LEVELS)[number], string> = {
  beginner: 'chat.knowledgeBeginner',
  intermediate: 'chat.knowledgeIntermediate',
  advanced: 'chat.knowledgeAdvanced',
  expert: 'chat.knowledgeExpert',
};

const KNOWLEDGE_DEFAULTS: Record<(typeof KNOWLEDGE_LEVELS)[number], string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
};

export function ProfileDraftSheet({
  visible,
  card,
  onClose,
  onChange,
  onSave,
  saving,
}: {
  visible: boolean;
  card: ProfileCard | null;
  onClose: () => void;
  onChange: (patch: Partial<ProfileCard>) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const canSave = card ? profileSaveEnabled(card) : false;

  const renderChips = (
    row: 'style' | 'risk' | 'turnover' | 'knowledge',
    options: readonly string[],
    selected: string | null | undefined,
    pick: (value: string) => void
  ) => (
    <View style={styles.optionRow}>
      {options.map((option) => {
        const isSel = selected === option;
        return (
          <TouchableOpacity
            key={option}
            testID={`profile-${row}-${option}`}
            style={[
              styles.option,
              {
                borderColor: isSel ? theme.colors.primary : theme.colors.border,
                backgroundColor: isSel
                  ? theme.colors.selectedBackground
                  : theme.colors.backgroundElevated,
              },
            ]}
            onPress={() => pick(option)}
            disabled={saving}
            accessibilityRole="button"
            accessibilityState={{ selected: isSel }}
          >
            <Text
              style={[
                styles.optionText,
                { color: isSel ? theme.colors.primary : theme.colors.text },
              ]}
            >
              {option}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close', { defaultValue: 'Close' })}
        />
        <View
          style={[styles.sheet, { backgroundColor: theme.colors.surface }]}
          testID="profile-sheet"
        >
          <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />
          {card && (
            <>
              <Text style={[styles.title, { color: theme.colors.text }]}>
                {t('chat.profileDraft', { defaultValue: 'Profile draft' })}
              </Text>

              <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
              >
                {/* Style — the seven SOUL archetypes by display name */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('chat.profileStyle', { defaultValue: 'Investing style' })}
                </Text>
                <View style={styles.wrapRow}>
                  {ARCHETYPES.map((a) => {
                    const isSel = card.style_name === a.displayName;
                    return (
                      <TouchableOpacity
                        key={a.code}
                        testID={`profile-style-${a.code}`}
                        style={[
                          styles.option,
                          styles.wrapOption,
                          {
                            borderColor: isSel ? theme.colors.primary : theme.colors.border,
                            backgroundColor: isSel
                              ? theme.colors.selectedBackground
                              : theme.colors.backgroundElevated,
                          },
                        ]}
                        onPress={() => onChange({ style_name: isSel ? null : a.displayName })}
                        disabled={saving}
                        accessibilityRole="button"
                        accessibilityState={{ selected: isSel }}
                      >
                        <Text
                          style={[
                            styles.optionText,
                            { color: isSel ? theme.colors.primary : theme.colors.text },
                          ]}
                        >
                          {a.displayName}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Risk — the portfolio_theses enum */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('chat.profileRisk', { defaultValue: 'Target risk' })}
                </Text>
                {renderChips('risk', RISK_LEVELS, card.target_risk_level, (value) =>
                  onChange({
                    target_risk_level:
                      card.target_risk_level === value
                        ? null
                        : (value as ProfileCard['target_risk_level']),
                  })
                )}

                {/* Turnover — optional; defaults ride the archetype */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('chat.profileTurnover', { defaultValue: 'Turnover tolerance' })}
                </Text>
                {renderChips('turnover', TURNOVER_TOLERANCES, card.turnover_tolerance, (value) =>
                  onChange({
                    turnover_tolerance: card.turnover_tolerance === value ? null : value,
                  })
                )}

                {/* Knowledge — the user_personas enum */}
                <Text style={[styles.label, { color: theme.colors.textSecondary }]}>
                  {t('chat.profileKnowledge', { defaultValue: 'Investing knowledge' })}
                </Text>
                <View style={styles.optionRow}>
                  {KNOWLEDGE_LEVELS.map((level) => {
                    const isSel = card.knowledge_level === level;
                    return (
                      <TouchableOpacity
                        key={level}
                        testID={`profile-knowledge-${level}`}
                        style={[
                          styles.option,
                          {
                            borderColor: isSel ? theme.colors.primary : theme.colors.border,
                            backgroundColor: isSel
                              ? theme.colors.selectedBackground
                              : theme.colors.backgroundElevated,
                          },
                        ]}
                        onPress={() => onChange({ knowledge_level: isSel ? null : level })}
                        disabled={saving}
                        accessibilityRole="button"
                        accessibilityState={{ selected: isSel }}
                      >
                        <Text
                          style={[
                            styles.optionText,
                            { color: isSel ? theme.colors.primary : theme.colors.text },
                          ]}
                        >
                          {t(KNOWLEDGE_LABEL_KEYS[level], {
                            defaultValue: KNOWLEDGE_DEFAULTS[level],
                          })}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </ScrollView>

              {/* Footer: close + confirm-to-save (enabled iff all three
                  required dimensions are set) */}
              <View style={styles.footer}>
                <TouchableOpacity
                  testID="profile-sheet-close"
                  style={[styles.closeButton, { borderColor: theme.colors.border }]}
                  onPress={onClose}
                  disabled={saving}
                >
                  <Text style={[styles.closeButtonText, { color: theme.colors.textSecondary }]}>
                    ✕ {t('common.close', { defaultValue: 'Close' })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID="profile-sheet-save"
                  style={[
                    styles.saveButton,
                    { backgroundColor: theme.colors.primary },
                    !canSave && styles.saveDisabled,
                  ]}
                  onPress={onSave}
                  disabled={!canSave || saving}
                  accessibilityRole="button"
                  accessibilityState={{ disabled: !canSave || saving }}
                >
                  <Text style={[styles.saveButtonText, { color: theme.colors.textInverse }]}>
                    {saving
                      ? t('common.saving', { defaultValue: 'Saving...' })
                      : t('chat.saveProfile', { defaultValue: 'Save profile' })}
                  </Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: theme.colors.overlay,
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    sheet: {
      maxHeight: '90%',
      borderTopLeftRadius: theme.radii.lg,
      borderTopRightRadius: theme.radii.lg,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.lg,
    },
    handle: {
      alignSelf: 'center',
      width: 40,
      height: 4,
      borderRadius: theme.radii.full,
      marginBottom: theme.spacing.sm,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      paddingHorizontal: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.lg,
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.md,
    },
    label: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: theme.spacing.sm,
      marginBottom: theme.spacing.xxs,
    },
    optionRow: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    wrapRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
    },
    option: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xs,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    wrapOption: {
      flex: 0,
      flexGrow: 1,
      paddingHorizontal: theme.spacing.sm,
    },
    optionText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      textAlign: 'center',
    },
    footer: {
      flexDirection: 'row',
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    closeButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    closeButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    saveButton: {
      flex: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.primary,
    },
    saveDisabled: {
      opacity: 0.5,
    },
    saveButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
  });
}
